'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import {
  Heart, MessageCircle, UserPlus, Download, Bell, Sparkles, CheckCheck, Trash2, Film
} from 'lucide-react';

type NotificationType = 'like' | 'comment' | 'follow' | 'download' | 'system' | 'mention';

interface Notification {
  id: string;
  type: NotificationType;
  read: boolean;
  timeAgo: string;
  actor?: {
    name: string;
    avatar: string;
    username: string;
    verified?: boolean;
  };
  clip?: {
    title: string;
    thumbnail: string;
    href: string;
  };
  message: string;
}

const typeConfig: Record<NotificationType, { icon: React.ElementType; color: string; bg: string }> = {
  like: { icon: Heart, color: '#f43f5e', bg: 'rgba(244,63,94,0.12)' },
  comment: { icon: MessageCircle, color: '#6C63FF', bg: 'rgba(108,99,255,0.12)' },
  follow: { icon: UserPlus, color: '#22d3ee', bg: 'rgba(34,211,238,0.12)' },
  download: { icon: Download, color: '#a3e635', bg: 'rgba(163,230,53,0.12)' },
  system: { icon: Sparkles, color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
  mention: { icon: MessageCircle, color: '#c084fc', bg: 'rgba(192,132,252,0.12)' },
};

const FILTER_TABS = ['All', 'Likes', 'Comments', 'Follows', 'Downloads'] as const;
type FilterTab = (typeof FILTER_TABS)[number];

const tabToType: Record<FilterTab, NotificationType | null> = {
  All: null,
  Likes: 'like',
  Comments: 'comment',
  Follows: 'follow',
  Downloads: 'download',
};

export default function NotificationsContent() {
  // Start with empty notifications — populated from real backend activity only
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [activeTab, setActiveTab] = useState<FilterTab>('All');

  const unreadCount = notifications.filter((n) => !n.read).length;

  const filtered = notifications.filter((n) => {
    const typeFilter = tabToType[activeTab];
    if (!typeFilter) return true;
    return n.type === typeFilter;
  });

  const markAllRead = () => setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  const markRead = (id: string) => setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
  const deleteNotification = (id: string) => setNotifications((prev) => prev.filter((n) => n.id !== id));

  return (
    <div className="max-w-screen-sm mx-auto px-4 pt-4 pb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-bold" style={{ color: 'var(--foreground)' }}>Notifications</h1>
          {unreadCount > 0 && (
            <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: 'var(--primary)', color: 'white' }}>
              {unreadCount}
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <button onClick={markAllRead} className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-all duration-150 hover:opacity-80" style={{ color: 'var(--primary)', background: 'rgba(108,99,255,0.1)' }}>
            <CheckCheck size={13} />
            Mark all read
          </button>
        )}
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1 mb-5" style={{ scrollbarWidth: 'none' }}>
        {FILTER_TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className="flex-shrink-0 text-xs font-semibold px-3.5 py-1.5 rounded-full transition-all duration-150"
            style={{ background: activeTab === tab ? 'var(--primary)' : 'var(--muted)', color: activeTab === tab ? 'white' : 'var(--muted-foreground)' }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Empty state */}
      {filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: 'var(--muted)' }}>
            <Bell size={28} style={{ color: 'var(--muted-foreground)' }} />
          </div>
          <p className="text-base font-semibold" style={{ color: 'var(--foreground)' }}>No notifications yet</p>
          <p className="text-sm text-center max-w-xs" style={{ color: 'var(--muted-foreground)' }}>
            When someone likes, comments, follows, or downloads your clips, you'll see it here.
          </p>
        </div>
      )}

      {/* Notification List */}
      {filtered.length > 0 && (
        <div className="flex flex-col gap-1">
          {filtered.map((notif) => {
            const cfg = typeConfig[notif.type];
            const TypeIcon = cfg.icon;

            return (
              <div
                key={notif.id}
                onClick={() => markRead(notif.id)}
                className="relative flex items-start gap-3 px-3 py-3 rounded-xl cursor-pointer transition-all duration-150 group"
                style={{
                  background: notif.read ? 'transparent' : 'rgba(108,99,255,0.06)',
                  border: notif.read ? '1px solid transparent' : '1px solid rgba(108,99,255,0.14)',
                }}
              >
                {!notif.read && <span className="absolute top-3.5 right-3 w-2 h-2 rounded-full" style={{ background: 'var(--primary)' }} />}

                <div className="relative flex-shrink-0 mt-0.5">
                  {notif.actor ? (
                    <>
                      <Link href={`/creator/${notif.actor.username}`} onClick={(e) => e.stopPropagation()}>
                        <div className="w-10 h-10 rounded-full overflow-hidden">
                          <AppImage src={notif.actor.avatar} alt={`${notif.actor.name} avatar`} width={40} height={40} className="w-full h-full object-cover" />
                        </div>
                      </Link>
                      <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: cfg.bg, border: '1.5px solid var(--background)' }}>
                        <TypeIcon size={10} style={{ color: cfg.color }} />
                      </span>
                    </>
                  ) : (
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: cfg.bg }}>
                      <TypeIcon size={18} style={{ color: cfg.color }} />
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0 pr-6">
                  <p className="text-sm leading-snug" style={{ color: 'var(--foreground)' }}>
                    {notif.actor && (
                      <Link href={`/creator/${notif.actor.username}`} onClick={(e) => e.stopPropagation()} className="font-bold hover:underline mr-1" style={{ color: 'var(--foreground)' }}>
                        {notif.actor.name}
                        {notif.actor.verified && <span className="ml-1 text-[10px]" style={{ color: 'var(--primary)' }}>✓</span>}
                      </Link>
                    )}
                    <span style={{ color: 'var(--muted-foreground)' }}>{notif.message}</span>
                  </p>
                  <p className="text-[11px] mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{notif.timeAgo}</p>
                </div>

                {notif.clip && (
                  <Link href={notif.clip.href} onClick={(e) => e.stopPropagation()} className="flex-shrink-0 w-12 h-9 rounded-lg overflow-hidden relative" style={{ border: '1px solid var(--border)' }}>
                    <AppImage src={notif.clip.thumbnail} alt={notif.clip.title} width={48} height={36} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.3)' }}>
                      <Film size={12} color="white" />
                    </div>
                  </Link>
                )}

                <button
                  onClick={(e) => { e.stopPropagation(); deleteNotification(notif.id); }}
                  className="absolute top-2 right-2 w-6 h-6 rounded-md items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-150 hidden group-hover:flex"
                  style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}
                  aria-label="Delete notification"
                >
                  <Trash2 size={11} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
